import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $findMatchingParent, $insertNodeToNearestRoot, mergeRegister } from '@lexical/utils'
import type {
  LexicalCommand,
  LexicalEditor
} from 'lexical'
import {
  $createParagraphNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  createCommand,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_UP_COMMAND
} from 'lexical'
import { useEffect, useState } from 'react'
import * as React from 'react'

import {
  $createLayoutContainerNode,
  $isLayoutContainerNode,
  LayoutContainerNode
} from '../nodes/layout-container-node'
import { $createLayoutItemNode, $isLayoutItemNode, LayoutItemNode } from '../nodes/layout-item-node'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const LAYOUTS = [
  { id: '1', name: '2 columns (equal width)', value: '1fr 1fr' },
  { id: '2', name: '2 columns (25% - 75%)', value: '1fr 3fr' },
  { id: '3', name: '3 columns (equal width)', value: '1fr 1fr 1fr' },
  { id: '4', name: '3 columns (25% - 50% - 25%)', value: '1fr 2fr 1fr' },
  { id: '5', name: '4 columns (equal width)', value: '1fr 1fr 1fr 1fr' }
]

export const INSERT_LAYOUT_COMMAND: LexicalCommand<string> = createCommand('INSERT_LAYOUT_COMMAND')

export const UPDATE_LAYOUT_COMMAND: LexicalCommand<{
  template: string;
  nodeKey: string;
}> = createCommand('UPDATE_LAYOUT_COMMAND')

export function InsertLayoutDialog({ editor, onClose }: { editor: LexicalEditor; onClose: () => void }): React.JSX.Element {
  const [layoutValue, setLayoutValue] = useState(LAYOUTS[0].value)

  const onClick = () => {
    editor.dispatchCommand(INSERT_LAYOUT_COMMAND, layoutValue)
    onClose()
  }

  return (
    <div className='flex flex-col gap-4'>
      <Select value={layoutValue} onValueChange={setLayoutValue}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select layout" />
        </SelectTrigger>
        <SelectContent>
          {LAYOUTS.map((l) => (
            <SelectItem key={l.id} value={l.value}>
              {l.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button onClick={onClick} className='ml-auto'>
        Insert
      </Button>
    </div>
  )
}

export default function LayoutPlugin(): null {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    if (!editor.hasNodes([LayoutContainerNode, LayoutItemNode])) {
      throw new Error(
        'LayoutPlugin: LayoutContainerNode, or LayoutItemNode not registered on editor'
      )
    }

    const onEscape = (before: boolean) => {
      const selection = $getSelection()
      if (
        $isRangeSelection(selection) &&
        selection.isCollapsed() &&
        selection.anchor.offset === 0
      ) {
        const container = $findMatchingParent(selection.anchor.getNode(), $isLayoutContainerNode)

        if ($isLayoutContainerNode(container)) {
          const parent = container.getParent()
          const child = parent && (before ? parent.getFirstChild() : parent?.getLastChild())
          const descendant = before
            ? container.getFirstDescendant()?.getKey()
            : container.getLastDescendant()?.getKey()

          if (parent !== null && child === container && selection.anchor.key === descendant) {
            if (before) {
              container.insertBefore($createParagraphNode())
            } else {
              container.insertAfter($createParagraphNode())
            }
          }
        }
      }

      return false
    }

    return mergeRegister(
      // When layout is the last child pressing down/right arrow will insert paragraph
      // below it to allow adding more content. It's similar what $insertBlockNode
      // (mainly for decorators), except it'll always be possible to continue adding
      // new content even if trailing paragraph is accidentally deleted
      editor.registerCommand(KEY_ARROW_DOWN_COMMAND, () => onEscape(false), COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, () => onEscape(false), COMMAND_PRIORITY_LOW),
      // When layout is the first child pressing up/left arrow will insert paragraph
      // above it to allow adding more content. It'll always be possible to continue adding
      // new content if leading paragraph is accidentally deleted
      editor.registerCommand(KEY_ARROW_UP_COMMAND, () => onEscape(true), COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_LEFT_COMMAND, () => onEscape(true), COMMAND_PRIORITY_LOW),
      editor.registerCommand(
        INSERT_LAYOUT_COMMAND,
        (template: string) => {
          editor.update(() => {
            const container = $createLayoutContainerNode(template)
            const itemsCount = getItemsCountFromTemplate(template)

            for (let i = 0; i < itemsCount; i++) {
              container.append($createLayoutItemNode().append($createParagraphNode()))
            }

            $insertNodeToNearestRoot(container)
            container.selectStart()
          })

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        UPDATE_LAYOUT_COMMAND,
        ({ template, nodeKey }) => {
          editor.update(() => {
            const container = $getNodeByKey(nodeKey)

            if (!$isLayoutContainerNode(container)) {
              return
            }

            const itemsCount = getItemsCountFromTemplate(template)
            const prevItemsCount = getItemsCountFromTemplate(container.getTemplateColumns())

            // Add or remove extra columns if new template does not match existing one
            if (itemsCount > prevItemsCount) {
              for (let i = prevItemsCount; i < itemsCount; i++) {
                container.append($createLayoutItemNode().append($createParagraphNode()))
              }
            } else if (itemsCount < prevItemsCount) {
              for (let i = prevItemsCount - 1; i >= itemsCount; i--) {
                const layoutItem = container.getChildAtIndex(i)

                if ($isLayoutItemNode(layoutItem)) {
                  layoutItem.remove()
                }
              }
            }

            container.setTemplateColumns(template)
          })

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ),
      // Structure enforcing transformers for each node type. In case nesting structure is not
      // "Container > Item" it'll unwrap nodes and convert it back
      // to regular content.
      editor.registerNodeTransform(LayoutItemNode, (node: LayoutItemNode) => {
        const parent = node.getParent()
        if (!$isLayoutContainerNode(parent)) {
          const children = node.getChildren()
          for (const child of children) {
            node.insertBefore(child)
          }
          node.remove()
        }
      }),
      editor.registerNodeTransform(LayoutContainerNode, (node: LayoutContainerNode) => {
        const children = node.getChildren()
        if (!children.every($isLayoutItemNode)) {
          for (const child of children) {
            node.insertBefore(child)
          }
          node.remove()
        }
      })
    )
  }, [editor])

  return null
}


function getItemsCountFromTemplate(template: string): number {
  return template.trim().split(/\s+/).length
}
